import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PendingApprovalsWidget from '../components/PendingApprovalsWidget';
import MTDPurchasesWidget from '../components/MTDPurchasesWidget';
import OutstandingPayablesWidget from '../components/OutstandingPayablesWidget';
import CashflowSnapshotWidget from '../components/CashflowSnapshotWidget';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { formatAmount, currencySymbol, companySettings } = useCurrency();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warrantyItems, setWarrantyItems] = useState([]);
  const [warrantyLoading, setWarrantyLoading] = useState(true);
  const [warrantyWindow, setWarrantyWindow] = useState(30);
  const [warrantyExporting, setWarrantyExporting] = useState(false);
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [aging, setAging] = useState(null);
  const [agingLoading, setAgingLoading] = useState(true);
  // Pending PO Receipts widget — POs that still have outstanding qty to receive
  const [pendingPOs, setPendingPOs] = useState([]);
  const [pendingPOsLoading, setPendingPOsLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarrantyExpiring = async (windowDays = warrantyWindow) => {
    try {
      setWarrantyLoading(true);
      const res = await api.get('/dashboard/warranty-expiring', { params: { days: windowDays, limit: 50 } });
      setWarrantyItems(res.data?.items || []);
    } catch (e) {
      // Silent fail — widget is optional
    } finally {
      setWarrantyLoading(false);
    }
  };

  const exportWarrantyExpiring = async () => {
    try {
      setWarrantyExporting(true);
      const res = await api.get('/dashboard/warranty-expiring/export', {
        params: { days: warrantyWindow },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').replace(/-/g, '');
      a.download = `warranty_expiring_${warrantyWindow}d_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Warranty export failed', e);
      alert('Failed to export warranty list. Please try again.');
    } finally {
      setWarrantyExporting(false);
    }
  };

  const fetchMaintenanceDue = async () => {
    try {
      const res = await api.get('/dashboard/maintenance-due', { params: { days: 30, limit: 10 } });
      setMaintenanceItems(res.data?.items || []);
    } catch (e) {
      // Silent fail
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const fetchAgingReport = async () => {
    try {
      const res = await api.get('/dashboard/aging-report');
      setAging(res.data);
    } catch (e) {
      // Silent fail
    } finally {
      setAgingLoading(false);
    }
  };

  const fetchPendingPOs = async () => {
    try {
      // Pull all POs and keep those still owed receipt (status 'partial' or 'open').
      const res = await api.get('/purchase-orders');
      const list = (res.data || []).filter(
        (po) => po.status === 'partial' || po.status === 'open'
      );
      // Sort: partial first (most urgent — already started), then open. Newest first within each.
      list.sort((a, b) => {
        const rank = (s) => (s === 'partial' ? 0 : 1);
        if (rank(a.status) !== rank(b.status)) return rank(a.status) - rank(b.status);
        return (b.po_date || '').localeCompare(a.po_date || '');
      });
      setPendingPOs(list.slice(0, 10));
    } catch (e) {
      /* Silent */
    } finally {
      setPendingPOsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    if (hasPermission(user, 'customers', 'view')) {
      fetchWarrantyExpiring();
    } else {
      setWarrantyLoading(false);
    }
    // Maintenance widget: users who can view customers (service call opportunities)
    if (hasPermission(user, 'customers', 'view')) {
      fetchMaintenanceDue();
    } else {
      setMaintenanceLoading(false);
    }
    // Aging widget: users who can view sales invoices
    if (hasPermission(user, 'sales_invoices', 'view')) {
      fetchAgingReport();
    } else {
      setAgingLoading(false);
    }
    // Pending PO Receipts: users who can view purchase orders
    if (hasPermission(user, 'purchase_orders', 'view')) {
      fetchPendingPOs();
    } else {
      setPendingPOsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="w-full h-full overflow-x-hidden overflow-y-auto">
      {/* Main Content - Mobile Responsive */}
      <main className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-6 lg:py-8 max-w-7xl lg:mx-auto overflow-x-hidden">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Welcome back, {user?.name}!</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Here's what's happening with your team today.</p>
        </div>

        {/* Pending Approvals — auto-hides when empty */}
        <PendingApprovalsWidget />

        {/* Cashflow at a Glance — combines Debtors-Aging + Creditors-Aging
            into a single Receivables vs Payables tile with Net WC. Shown
            only to users who can view both sides. */}
        {hasPermission(user, 'sales_invoices', 'view') &&
          hasPermission(user, 'supplier_payments', 'view') && <CashflowSnapshotWidget />}

        {/* MTD Purchases VAT-capture summary (Purchase Entries + Other Purchases).
            Visible to anyone who can view Purchase Entries — i.e. admins,
            super-admins and data_entry users. */}
        {hasPermission(user, 'purchase_entries', 'view') && <MTDPurchasesWidget />}

        {/* Outstanding Payables — sums unpaid PO balances per supplier
            currency. Visible to anyone who can view supplier payments. */}
        {hasPermission(user, 'supplier_payments', 'view') && <OutstandingPayablesWidget />}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 mb-4 sm:mb-6 lg:mb-8" data-testid="dashboard-stats" data-mobile-keep-grid>
          {hasPermission(user, 'users', 'view') && (
            <Button
              onClick={() => window.location.href = '/users'}
              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-3 sm:py-4"
              data-testid="users-button"
            >
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="font-semibold text-sm sm:text-base">Manage Users</span>
              </div>
            </Button>
          )}
          {/* Activities — anyone with activities view */}
          {hasPermission(user, 'activities', 'view') && (
            <Button
              onClick={() => window.location.href = '/activities'}
              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-3 sm:py-4"
              data-testid="activities-button"
            >
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-semibold text-sm sm:text-base">{(user?.view_scope || 'all') === 'own' ? 'My Activities' : 'Activities'}</span>
              </div>
            </Button>
          )}
          
          {/* Customers - All Users */}
          <Button
            onClick={() => window.location.href = '/customers'}
            className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-4"
            data-testid="customers-button"
          >
            <div className="flex flex-col items-center">
              <svg className="w-8 h-8 mb-2 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="font-semibold">Customers</span>
            </div>
          </Button>
          
          {/* Products — anyone with products view */}
          {hasPermission(user, 'products', 'view') && (
            <Button
              onClick={() => window.location.href = '/products'}
              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-3 sm:py-4"
              data-testid="products-button"
            >
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span className="font-semibold text-sm sm:text-base">Products</span>
              </div>
            </Button>
          )}
          
          {/* Leads — anyone with leads view */}
          {hasPermission(user, 'leads', 'view') && (
            <Button
              onClick={() => window.location.href = '/leads'}
              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-3 sm:py-4"
              data-testid="leads-button"
            >
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span className="font-semibold text-sm sm:text-base">Leads</span>
              </div>
            </Button>
          )}
          {/* Teams — anyone with teams view */}
          {hasPermission(user, 'teams', 'view') && (
            <Button
              onClick={() => window.location.href = '/teams'}
              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 h-auto py-3 sm:py-4"
              data-testid="teams-button"
            >
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-semibold text-sm sm:text-base">{hasPermission(user, 'teams', 'edit') ? 'Manage Teams' : 'My Team'}</span>
              </div>
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 mb-4 sm:mb-6" data-testid="dashboard-stats" data-mobile-keep-grid>
              {/* Total Leads — users with leads view */}
              {hasPermission(user, 'leads', 'view') && (
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-total-leads">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Leads</p>
                        <h3 className="text-3xl font-bold mt-2 text-cyan-600">{stats?.total_leads || 0}</h3>
                      </div>
                      <div className="p-3 rounded-xl bg-cyan-100">
                        <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </div>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">🎯 Closed Won</span>
                        <span className="text-lg font-bold text-green-600">{stats?.closed_won_leads || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">💰 Project Value</span>
                        <span className="text-lg font-bold text-green-600">
                          {formatAmount(stats?.total_project_value || 0)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Total Activities — users with activities view */}
              {hasPermission(user, 'activities', 'view') && (
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-total-activities">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Activities</p>
                      <h3 className="text-3xl font-bold mt-2 text-purple-600">{stats?.total_activities || 0}</h3>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-100">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                  </div>
                  <div className="space-y-2 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">⏳ Pending</span>
                      <span className="text-sm font-bold text-amber-600">{stats?.pending_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">🔄 In Progress</span>
                      <span className="text-sm font-bold text-blue-600">{stats?.in_progress_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">✅ Completed</span>
                      <span className="text-sm font-bold text-green-600">{stats?.completed_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-600">💰 Total Value</span>
                      <span className="text-sm font-bold text-green-600">{formatAmount(stats?.total_activities_value || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Total Users — users with users view */}
              {hasPermission(user, 'users', 'view') && (
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-total-users">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Users</p>
                        <h3 className="text-3xl font-bold mt-2 text-blue-600">{stats?.total_users || 0}</h3>
                      </div>
                      <div className="p-3 rounded-xl bg-blue-100">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Active Users — users with users view */}
              {hasPermission(user, 'users', 'view') && (
                <Card className="hover:shadow-lg transition-shadow" data-testid="stat-active-users">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Active Users</p>
                        <h3 className="text-3xl font-bold mt-2 text-green-600">{stats?.active_users || 0}</h3>
                      </div>
                      <div className="p-3 rounded-xl bg-green-100">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {/* Warranty Expiring — users with customers view */}
              {hasPermission(user, 'customers', 'view') && (
                <Card data-testid="warranty-expiring-card" className="border-amber-200 order-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span>⏰ Warranty Expiring Soon</span>
                      {warrantyItems.length > 0 && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          {warrantyItems.length}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>Next {warrantyWindow} days · upsell & service opportunities</CardDescription>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <div className="inline-flex rounded border border-amber-200 overflow-hidden text-xs" data-testid="warranty-window-toggle">
                        {[30, 60, 90].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => { setWarrantyWindow(d); fetchWarrantyExpiring(d); }}
                            className={`px-2.5 py-1 ${warrantyWindow === d ? 'bg-amber-600 text-white' : 'bg-white text-amber-700 hover:bg-amber-50'}`}
                            data-testid={`warranty-window-${d}d`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={exportWarrantyExpiring}
                        disabled={warrantyExporting || warrantyItems.length === 0}
                        className="text-xs px-2.5 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                        title="Export call-list as Excel (.xlsx)"
                        data-testid="warranty-export-excel-btn"
                      >
                        {warrantyExporting ? '⏳ Exporting…' : '📥 Export Excel'}
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {warrantyLoading ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : warrantyItems.length === 0 ? (
                      <p className="text-sm text-gray-500">No warranties expiring in the next 30 days. 🎉</p>
                    ) : (
                      <ul className="divide-y divide-gray-100 -mx-2">
                        {warrantyItems.slice(0, 5).map((w, i) => (
                          <li
                            key={`${w.product_id}-${w.serial_number}-${i}`}
                            className="px-2 py-2 hover:bg-amber-50 cursor-pointer"
                            onClick={() => { window.location.href = '/customers'; }}
                            data-testid={`warranty-expiring-row-${i}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {w.customer_name || 'Unknown customer'}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {w.product_name} · <span className="font-mono">{w.serial_number}</span>
                                </p>
                              </div>
                              <span
                                className={`shrink-0 text-xs px-2 py-0.5 rounded ${
                                  w.days_left <= 7
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-amber-100 text-amber-800'
                                }`}
                              >
                                {w.days_left === 0 ? 'today' : `${w.days_left}d left`}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    {warrantyItems.length > 5 && (
                      <button
                        onClick={() => { window.location.href = '/customers'; }}
                        className="mt-3 text-xs text-blue-600 hover:underline"
                        data-testid="warranty-expiring-view-all"
                      >
                        View all {warrantyItems.length} →
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Team Overview — users with teams view */}
              {hasPermission(user, 'teams', 'view') && (
                <Card className="order-4">
                  <CardHeader>
                    <CardTitle>Team Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Sales Team</span>
                        <span className="text-2xl font-bold text-blue-600">{stats?.total_sales || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Support Team</span>
                        <span className="text-2xl font-bold text-green-600">{stats?.total_support || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Teams</span>
                        <span className="text-2xl font-bold text-purple-600">{stats?.total_teams || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="order-5">
                <CardHeader>
                  <CardTitle>Activities Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Pending</span>
                      <span className="text-2xl font-bold text-amber-600">{stats?.pending_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">In Progress</span>
                      <span className="text-2xl font-bold text-blue-600">{stats?.in_progress_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Completed</span>
                      <span className="text-2xl font-bold text-green-600">{stats?.completed_activities || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total</span>
                      <span className="text-2xl font-bold text-purple-600">{stats?.total_activities || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Maintenance Due Widget — users with customers view */}
              {hasPermission(user, 'customers', 'view') && (
                <Card data-testid="maintenance-due-card" className="border-sky-200 order-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span>🔧 Next Maintenance Due</span>
                      {maintenanceItems.length > 0 && (
                        <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">
                          {maintenanceItems.length}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Next 30 days · service call opportunities
                      {maintenanceItems.some((m) => m.is_overdue) && (
                        <span className="ml-2 text-red-600 font-medium">
                          · {maintenanceItems.filter((m) => m.is_overdue).length} overdue
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {maintenanceLoading ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : maintenanceItems.length === 0 ? (
                      <p className="text-sm text-gray-500">No machines due for maintenance in the next 30 days. 🎉</p>
                    ) : (
                      <ul className="divide-y divide-gray-100 -mx-2">
                        {maintenanceItems.slice(0, 5).map((m, i) => (
                          <li
                            key={`${m.product_id}-${m.serial_number}-${i}`}
                            className="px-2 py-2 hover:bg-sky-50 cursor-pointer"
                            onClick={() => { window.location.href = '/customers'; }}
                            data-testid={`maintenance-due-row-${i}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {m.customer_name || 'Unknown customer'}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {m.product_name} · <span className="font-mono">{m.serial_number}</span>
                                </p>
                              </div>
                              <span
                                className={`shrink-0 text-xs px-2 py-0.5 rounded ${
                                  m.is_overdue
                                    ? 'bg-red-100 text-red-700'
                                    : m.days_until <= 7
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-sky-100 text-sky-700'
                                }`}
                              >
                                {m.is_overdue
                                  ? `${Math.abs(m.days_until)}d overdue`
                                  : m.days_until === 0
                                  ? 'today'
                                  : `in ${m.days_until}d`}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    {maintenanceItems.length > 5 && (
                      <button
                        onClick={() => { window.location.href = '/customers'; }}
                        className="mt-3 text-xs text-blue-600 hover:underline"
                        data-testid="maintenance-due-view-all"
                      >
                        View all {maintenanceItems.length} →
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending PO Receipts — users with purchase_orders view */}
              {hasPermission(user, 'purchase_orders', 'view') && (
                <Card data-testid="pending-pos-card" className="border-orange-200 order-3">
                  <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <span>📦 Pending PO Receipts</span>
                        {pendingPOs.length > 0 && (
                          <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
                            {pendingPOs.length}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>POs awaiting goods receipt (open or partial)</CardDescription>
                    </div>
                    <a
                      href="/purchase-entries"
                      className="text-xs text-orange-700 hover:underline"
                      data-testid="pending-pos-receive-btn"
                    >
                      Record Receipt →
                    </a>
                  </CardHeader>
                  <CardContent>
                    {pendingPOsLoading ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : pendingPOs.length === 0 ? (
                      <p className="text-sm text-gray-500">All POs fully received. 🎉</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {pendingPOs.map((po) => (
                          <div
                            key={po.id}
                            className="flex items-center justify-between gap-2 p-2 border rounded text-sm hover:bg-orange-50"
                            data-testid={`pending-po-${po.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{po.po_number}</div>
                              <div className="text-xs text-gray-500 truncate">{po.supplier_name}</div>
                              {po.status === 'partial' && po.balance_summary && (
                                <div className="text-xs text-orange-700 truncate" title={po.balance_summary}>
                                  Pending: {po.balance_summary}
                                </div>
                              )}
                            </div>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${
                                po.status === 'partial'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {po.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Aging Report Widget — users with sales_invoices view */}
              {hasPermission(user, 'sales_invoices', 'view') && (
                <Card data-testid="aging-report-card" className="border-purple-200 order-3">
                  <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <span>📊 Receivables Aging</span>
                        {aging?.total_invoices > 0 && (
                          <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                            {aging.total_invoices}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Outstanding: {agingLoading ? '…' : formatAmount(aging?.total_outstanding || 0)}
                        {(user?.view_scope || 'all') !== 'all' && <span className="ml-2 text-xs text-gray-500">· your invoices</span>}
                      </CardDescription>
                    </div>
                    {!agingLoading && aging?.total_invoices > 0 && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.get('/dashboard/aging-report/export', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            const ts = new Date().toISOString().slice(0, 10);
                            link.setAttribute('download', `aging_report_${ts}.xlsx`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (e) {
                            alert(e.response?.data?.detail || 'Export failed');
                          }
                        }}
                        className="shrink-0 text-xs px-3 py-1.5 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 font-medium"
                        data-testid="aging-export-btn"
                      >
                        ⬇ Excel
                      </button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {agingLoading ? (
                      <p className="text-sm text-gray-500">Loading…</p>
                    ) : !aging || aging.total_invoices === 0 ? (
                      <p className="text-sm text-gray-500">No outstanding invoices. ✅</p>
                    ) : (
                      <>
                        <div className="space-y-1.5 mb-3">
                          {[
                            { key: 'current', dot: 'bg-gray-400',   label: 'Not yet due' },
                            { key: '0_30',    dot: 'bg-blue-500',   label: '0–30 days' },
                            { key: '31_60',   dot: 'bg-amber-500',  label: '31–60 days' },
                            { key: '61_90',   dot: 'bg-orange-500', label: '61–90 days' },
                            { key: 'over_90', dot: 'bg-red-600',    label: '90+ days' },
                          ].map((b) => {
                            const data = aging.buckets[b.key];
                            return (
                              <div
                                key={b.key}
                                onClick={() => { window.location.href = `/sales-invoices?bucket=${b.key}`; }}
                                className="flex items-center justify-between gap-3 px-2 py-1.5 rounded hover:bg-purple-50 cursor-pointer"
                                data-testid={`aging-bucket-${b.key}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter') window.location.href = `/sales-invoices?bucket=${b.key}`; }}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className={`h-2.5 w-2.5 rounded-full ${b.dot} shrink-0`} />
                                  <span className="text-sm text-gray-700 truncate">{b.label}</span>
                                  <span className="text-xs text-gray-400 shrink-0">· {data.count}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                                  {formatAmount(data.amount)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {aging.items?.length > 0 && (
                          <div className="border-t pt-2">
                            <p className="text-xs font-semibold text-gray-600 mb-1">Most overdue</p>
                            <ul className="divide-y divide-gray-100 -mx-2">
                              {aging.items.slice(0, 5).map((it, i) => (
                                <li
                                  key={`${it.invoice_number}-${i}`}
                                  className="px-2 py-1.5 hover:bg-purple-50 cursor-pointer flex items-center justify-between gap-2"
                                  onClick={() => { window.location.href = '/sales-invoices'; }}
                                  data-testid={`aging-item-${i}`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {it.customer_name || 'Unknown'}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      #{it.invoice_number} · {formatAmount(it.amount)}
                                    </p>
                                  </div>
                                  <span
                                    className={`shrink-0 text-xs px-2 py-0.5 rounded ${
                                      it.days_overdue > 90
                                        ? 'bg-red-100 text-red-700'
                                        : it.days_overdue > 60
                                        ? 'bg-orange-100 text-orange-700'
                                        : it.days_overdue > 30
                                        ? 'bg-amber-100 text-amber-800'
                                        : it.days_overdue > 0
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {it.days_overdue > 0
                                      ? `${it.days_overdue}d overdue`
                                      : it.days_overdue === 0
                                      ? 'due today'
                                      : `due in ${-it.days_overdue}d`}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Monitoring — users with location_tracking view */}
              {hasPermission(user, 'location_tracking', 'view') && (
                <Card className="order-6">
                  <CardHeader>
                    <CardTitle>Monitoring</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Geofences</span>
                        <span className="text-2xl font-bold text-purple-600">{stats?.total_geofences || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Today</span>
                        <span className="text-2xl font-bold text-green-600">{stats?.active_users || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
